/**
 * The catalog machinery — **Domain A** of `docs/i18n-design.md` §2, which asks
 * for "ordinary i18n. ICU MessageFormat catalogs per locale, `Intl` for
 * dates/money everywhere, locale negotiated browser → user preference → tenant
 * default. Ship EN + ES catalogs; **the framework is the point**."
 *
 * This is the framework, and it is about sixty lines, on purpose.
 *
 * ## Why this file is a copy of `apps/site/src/messages/format.ts`
 *
 * Because **apps do not cross-import** (dev.md §3). `apps/site` is a Vite bundle
 * served from a Cloud Storage bucket on hundreds of tenant hostnames and this is
 * a Firebase-hosted console for one seat; they share a monorepo and nothing
 * else, and a `packages/i18n` created to hold sixty lines would be a package
 * whose only job is to make two apps depend on each other. The duplication is
 * deliberate and it is stated here rather than discovered later — if a third
 * surface copies it a third time, *that* is the moment to promote it.
 *
 * ## Why there is no i18n library
 *
 * Two of `apps/site`'s three reasons apply unchanged here, and the third
 * changes shape.
 *
 * **The compiler already does the hard part.** The thing an i18n library
 * actually buys is *safety* — a missing key, a wrong placeholder — and it buys
 * it at runtime, as a fallback or a `[object Object]` on somebody's screen. Here
 * `es` is declared `: Messages`, so a missing key is a **compile error** and a
 * placeholder with the wrong shape is a compile error, before anything ships.
 *
 * **ICU's plural syntax is a second language to be wrong in.** A catalog entry
 * here is a plain string when it takes nothing and a **function** when it takes
 * something. Spanish agreement — `1 entrega` against `15 entregas` — is
 * ordinary TypeScript, read and tested like any other code.
 *
 * **The bundle.** The guest site cares because a stranger on hotel wifi waits
 * for it; this console does not, because it is a laptop at a desk. What this
 * surface cares about instead is that the *console* must never be the vaguest
 * description of what happened — see `en.ts` on the sentences that are
 * deliberately not ours to write.
 *
 * What is deliberately kept from ICU's model: **the message decides**, not the
 * call site. A component asks for `t('keys.notEnabled', { module })` and never
 * assembles a sentence out of fragments, because word order is exactly what a
 * translation changes.
 */

/**
 * One entry. A bare string takes nothing; a function takes exactly what its
 * own parameter object declares, and every locale must declare the same one.
 *
 * `never` as the parameter type is what makes the constraint accept any
 * concrete parameter object contravariantly — it is a bound, not a value that
 * ever reaches a template.
 */
export type Entry = string | ((params: never) => string);

export type Catalog = Readonly<Record<string, Entry>>;

/** `[]` for a plain string, `[TheParams]` for a function — so `t` needs no overloads. */
export type ParamsOf<C, K extends keyof C> = C[K] extends (params: infer P) => string ? [P] : [];

export interface Translate<C extends Catalog> {
  <K extends keyof C>(key: K, ...params: ParamsOf<C, K>): string;
}

/**
 * Bind a catalog to a `t`.
 *
 * There is no fallback chain and no "missing key" branch, because there cannot
 * be a missing key: every catalog is checked against the English one at compile
 * time. A runtime fallback here would be a place for a Spanish console to
 * quietly serve an English sentence, which is precisely the surprise the type is
 * for.
 */
export function translator<C extends Catalog>(catalog: C): Translate<C> {
  return <K extends keyof C>(key: K, ...params: ParamsOf<C, K>): string => {
    const entry: Entry | undefined = catalog[key];
    if (typeof entry === 'function') {
      const render = entry as (p: unknown) => string;
      return render(params[0]);
    }
    // `entry` cannot actually be `undefined` — `K extends keyof C` — but the
    // index signature on `Catalog` says it might, and asserting it away would be
    // the one place a real missing key could hide. An empty string on screen is
    // louder than a crash mid-cutover and quieter than a lie.
    return entry ?? '';
  };
}

/**
 * The two-form cardinal split, for the languages that have one.
 *
 * English and Spanish both distinguish exactly *one* from *everything else*
 * (`0 deliveries`, `0 entregas`), so one helper serves both catalogs. It lives
 * here rather than in either of them because a helper per language that did the
 * same thing would drift.
 *
 * A language with more categories — Polish has three, Arabic six — does **not**
 * extend this function. It writes its own arithmetic inside its own catalog
 * entries, which is the whole reason an entry is a function: the plural rule
 * belongs to the sentence, in the language the sentence is in.
 */
export function plural(count: number, one: string, other: string): string {
  return count === 1 ? one : other;
}
